import { CalendarDays, CalendarX, FileSearch } from 'lucide-react'
import { useState } from 'react'

import { session } from '@/api/session'
import type { Branch, BusinessDay } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { branchLabel } from '@/lib/branch'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'

import { LedgerAuditTrail } from './audit-trail-panel'
import { BranchAccessBadge, BranchReachNotice } from './branch-reach'
import { BusinessDayRegister } from './business-day-register'
import { CloseBusinessDaySection } from './close-business-day'
import { BUSINESS_DAY_STATUS_LABEL, BUSINESS_DAY_STATUS_TONE } from './constants'
import { OpenBusinessDayCard } from './open-business-day'
import { useLedgerPermissions } from './permissions'
import { useBranchBusinessDay, useLedgerBranchReach } from './queries'
import { ReopenBusinessDayAction } from './reopen-business-day'

/**
 * Where each branch is in its trading day.
 *
 * The date on these cards is the ledger's, never the browser's. A branch that has not closed
 * Friday is still working in Friday over the weekend, and that discrepancy is the reason this
 * screen exists rather than a reason to correct it. Where no day is open, no date is shown at all
 * — substituting today's would show an operator a day they cannot post into as though they could.
 */
function BusinessDayCard({ branch, canReadAudit }: { branch: Branch; canReadAudit: boolean }) {
  const [showAudit, setShowAudit] = useState(false)
  const query = useBranchBusinessDay(branch.id)

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <CalendarDays className="size-4 text-muted" aria-hidden />
            {branchLabel(branch)}
            {branch.isHomeBranch ? <Badge tone="brand">Home</Badge> : null}
          </CardTitle>
          <BranchAccessBadge branch={branch} />
        </div>
        {query.data ? (
          <Badge tone={BUSINESS_DAY_STATUS_TONE[query.data.status] ?? 'warning'}>
            {BUSINESS_DAY_STATUS_LABEL[query.data.status] ?? query.data.status}
          </Badge>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3">
        {query.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="This branch's day could not be read"
          />
        ) : (
          <>
            <BusinessDayFacts day={query.data} />

            {/*
              The morning-after affordance: nothing open, and the branch's newest day is the closed
              one — the only state a reopen can ever succeed from, so the only state that shows it.
            */}
            {query.data.businessDate === null && query.data.lastClosedBusinessDate ? (
              <ReopenBusinessDayAction
                branchId={branch.id}
                businessDate={query.data.lastClosedBusinessDate}
              />
            ) : null}

            {canReadAudit ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setShowAudit((current) => !current)}>
                  <FileSearch />
                  {showAudit ? 'Hide who opened it' : 'Who opened it'}
                </Button>

                {showAudit ? (
                  <LedgerAuditTrail
                    entityType="BusinessDay"
                    entityId={auditIdentifier(branch.id, query.data)}
                    canRead={canReadAudit}
                    emptyHint={
                      auditIdentifier(branch.id, query.data) === ''
                        ? 'This branch has never opened a day, so there is nothing recorded against one.'
                        : 'A day opened before auditing was in place carries no entry.'
                    }
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The identifier the ledger files a business day under in the audit trail: the branch and the
 * date, joined by a colon, exactly as the handler writes it. Empty when the branch has no date at
 * all — never opened, never closed — because there is then no day to ask about.
 */
function auditIdentifier(branchId: string, day: BusinessDay): string {
  const date = day.businessDate ?? day.lastClosedBusinessDate ?? null
  return date === null ? '' : `${branchId}:${date}`
}

function BusinessDayFacts({ day }: { day: BusinessDay }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
      <div className="col-span-2 min-w-0">
        <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Business date</dt>
        <dd className="mt-0.5 text-sm font-medium text-heading">
          {day.businessDate === null ? (
            <span className="flex items-center gap-1.5 text-body">
              <CalendarX className="size-4 text-warning-text" aria-hidden />
              No business day open
            </span>
          ) : (
            <span className="numeric tabular-nums">{formatBusinessDate(day.businessDate)}</span>
          )}
        </dd>
      </div>

      <div className="min-w-0">
        <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Opened</dt>
        <dd className="mt-0.5 text-sm text-heading">
          {day.openedAtUtc ? (
            <span className="numeric tabular-nums">{formatDateTime(day.openedAtUtc)}</span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </dd>
      </div>

      <div className="min-w-0">
        <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Postings</dt>
        <dd className="mt-0.5 text-sm text-heading">
          {/*
            Sent by the server rather than derived from the status. A screen that guessed this
            wrong would either block real work or invite a transaction the ledger refuses after the
            customer has handed over cash.
          */}
          {day.acceptsPostings ? 'Accepted' : 'Refused'}
        </dd>
      </div>

      {day.businessDate === null ? (
        <div className="col-span-2 min-w-0">
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Last traded</dt>
          <dd className="mt-0.5 text-sm text-heading">
            {/*
              Sent only when nothing is open, and the two nulls mean different things: a date says
              the branch closed normally and simply has not opened today, while nothing at all says
              this branch has never opened a day.
            */}
            {day.lastClosedBusinessDate ? (
              <span className="numeric tabular-nums">
                {formatBusinessDate(day.lastClosedBusinessDate)}
              </span>
            ) : (
              <span className="text-body">This branch has never opened a day</span>
            )}
          </dd>
        </div>
      ) : null}
    </dl>
  )
}

/**
 * The position: where every branch this user can reach is in its trading day, and the form that
 * opens one.
 *
 * The read adapts to any width; the form refuses below `lg` — see {@link OpenBusinessDayCard}.
 */
export function BusinessDaySection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const defaultBranchId = session.getBranchId() ?? reach.homeBranchId

  return (
    <div className="space-y-4">
      <BranchReachNotice reach={reach} />

      {reach.isPending ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-52 w-full" />
        </div>
      ) : reach.branches.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-sm text-body">
              No branch is recorded against this session, so there is no trading day to show. That is
              an access problem rather than a ledger one — the branches a user may operate in are
              granted by an administrator.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {reach.branches.map((branch) => (
            <BusinessDayCard key={branch.id} branch={branch} canReadAudit={permissions.canReadAudit} />
          ))}
        </div>
      )}

      <OpenBusinessDayCard
        branches={reach.branches}
        defaultBranchId={defaultBranchId}
        canOpen={permissions.canOpenDay}
      />
    </div>
  )
}

type BusinessDayTab = 'position' | 'register' | 'close'

const BUSINESS_DAY_PANEL_ID = 'ledger-business-day-panel'

/**
 * Business day control, whole.
 *
 * Three sections, in the order the day is worked: where the branches stand and how a day is opened,
 * the register behind that, and the close. They are tabs rather than sub-routes for the same reason
 * the module's landing page uses tabs — `/ledger/business-days` is registered in the router and its
 * children are not, so a typed link to one would not compile until the integrator registers it. The
 * strip switches in place until then and the sections stay independently exportable.
 *
 * The width policy is decided per section rather than for the screen: reading whether a branch is
 * trading is exactly the question somebody has from a phone, and closing a day is not.
 */
export function BusinessDayScreen() {
  const permissions = useLedgerPermissions()
  const [tab, setTab] = useState<BusinessDayTab>('position')

  const tabs: TabDefinition<BusinessDayTab>[] = [
    { id: 'position', label: 'Position' },
    { id: 'register', label: 'Register' },
    { id: 'close', label: 'Close the day' },
  ]

  return (
    <div className="space-y-4">
      <TabStrip<BusinessDayTab>
        tabs={tabs}
        active={tab}
        onChange={setTab}
        panelId={BUSINESS_DAY_PANEL_ID}
        label="Business day sections"
      />

      <div
        id={BUSINESS_DAY_PANEL_ID}
        role="tabpanel"
        aria-labelledby={`${BUSINESS_DAY_PANEL_ID}-tab-${tab}`}
      >
        {tab === 'position' ? <BusinessDaySection /> : null}
        {tab === 'register' ? <BusinessDayRegister /> : null}
        {tab === 'close' ? <CloseBusinessDaySection /> : null}
      </div>

      {permissions.canCloseDay && tab !== 'close' ? (
        <p className="text-xs text-muted">
          A day that is not closed is a day whose figures are not reported. The close is under
          &ldquo;Close the day&rdquo;.
        </p>
      ) : null}
    </div>
  )
}
